// author: Caleb Yearsley (caleb.yearsley@tecuity.com)
// date: 2019-09-04

import "./PermissionManager.less";

import * as React from "react";

import { Button, Modal, Popconfirm } from "antd";
import ButtonGroup from "antd/lib/button/button-group";
import Table, { ColumnProps } from "antd/lib/table";
import { Guid } from "guid-typescript";
import { ComponentTemplate, Permission, Section, User } from "lib/data/model";

import { SectionApi } from "lib/api";
import { UserApi } from "lib/api/security/user";
import { IBaseProps } from "lib/data/interfaces";
import { Right } from "lib/data/model/enumerations";
import { FuturePermission } from "lib/data/model/security/FuturePermission";
import { MultipleRecordPermission } from "lib/data/model/security/MultipleRecordPermission";
import { SingleRecordPermission } from "lib/data/model/security/SingleRecordPermission";
import cloneDeep from "lodash/cloneDeep";
import { curry, __ } from "lodash/fp";
import { Link } from "react-router-dom";
import { PermissionManagerDefintion, PermissionManagerForm } from "./PermissionManagerForm/PermissionManagerForm";
import { AdminPagePermission } from "lib/data/model/security/AdminPagePermission";

interface IPermissionManagerProps extends IBaseProps {

	onChange: (updatedPermissions: Permission[]) => void;
	currentUserPermissions: Permission[];
	targetUserPermissions: Permission[];
}

interface IPermissionManagerState {

	targetUserPermissions: ICustomPermission[];
	managingPermission: ICustomPermission | null;
	isPermissionFormOpen: boolean;
	permittedUsers: User[];
}

export interface ICustomPermission {
	permission: Permission;
	permissionId: Guid;
	section?: Section;
}

interface IPermissionRow extends ICustomPermission {
	type: string;
	right: string;
	records: React.ReactNode;
}

export class PermissionManager extends React.Component<IPermissionManagerProps, IPermissionManagerState> {

	private permissionFormReference?: PermissionManagerDefintion;

	public constructor(props: IPermissionManagerProps) {

		super(props);

		this.state = {
			targetUserPermissions: [],
			managingPermission: null,
			isPermissionFormOpen: false,
			permittedUsers: []
		};
	}

	public componentWillMount() {

		UserApi
			.getAllUsers()
			.then((response) => {

				this.setState({
					permittedUsers: response
				});
			});
	}

	public async componentWillReceiveProps(props: IPermissionManagerProps) {

		let customPermissions: ICustomPermission[] = [];
		const sectionRequestPromises: Array<Promise<any>> = [];

		props.targetUserPermissions.forEach((permission) => {

			if (permission.$type === SingleRecordPermission.$type && permission.recordType === Section.$type) {

				sectionRequestPromises.push(
					SectionApi.getSection(permission.recordId, true)
					.then((section) => {

						if (section !== undefined) {

							customPermissions.push({
								permission,
								permissionId: Guid.create(),
								section
							});
						}
					})
				);
			}
			else {

				sectionRequestPromises.push(
					Promise.resolve()
						.then(() => {

							customPermissions.push({
								permission,
								permissionId: Guid.create()
							});
						})
				);
			}
		});

		await Promise.all(sectionRequestPromises);

		this.setState({
			targetUserPermissions: customPermissions
		});
	}

	public render() {

		const permissionTableColumns: Array<ColumnProps<IPermissionRow>> = [
			{
				title: "Record Type",
				dataIndex: "type"
			},
			{
				title: "Right",
				dataIndex: "right"
			},
			{
				title: "Scope",
				dataIndex: "records"
			},
			{
				key: "permission-actions-column",
				width: 100,
				align: "center",
				render: (record) => (
					<ButtonGroup>
						<Button
							icon="edit"
							onClick={() => this.onPermissionEditClick(record)}
						/>
						<Popconfirm
								title="Are you sure delete this permission?"
								onConfirm={curry(this.onPermissionDeleteClick)(__, record)}
								okText="Yes"
								cancelText="No"
						>
							<Button
								key={`delete-user-button`}
								icon="delete"
								type="danger"
							/>
						</Popconfirm>
					</ButtonGroup>
				)
			}
		];

		const className = `permission-manager${this.props.className ? ` ${this.props.className}` : ""}`;

		return (
			<section className={className}>
				<Modal
					className="permission-manager-modal"
					visible={this.state.isPermissionFormOpen}
					onCancel={this.onPermissionFormCancel}
					okText="Save"
					onOk={this.onPermissionFormSaveClick}
				>
					{
						this.state.isPermissionFormOpen === true
						?
						(
							<PermissionManagerForm
								wrappedComponentRef={(form: PermissionManagerDefintion) => this.permissionFormReference = form}
								permissionToLoad={this.state.managingPermission}
								onSave={this.onPermissionFormSave}
								currentUserPermissions={this.props.currentUserPermissions}
								targetUserPermissions={this.props.targetUserPermissions}
								permittedUsers={this.state.permittedUsers}
							/>
						)
						:
						null
					}
				</Modal>
				<div className="action-container">
					<Button
						type="primary"
						icon="plus"
						onClick={this.onPermissionNewClick}
					>
						Add Permission
					</Button>
				</div>
				<Table<IPermissionRow>
					columns={permissionTableColumns}
					dataSource={this.formatPermissionData(this.state.targetUserPermissions)}
					bordered={true}
					size="small"
					rowKey={(record) => record.permissionId.toString()}
					pagination={false}
				/>
			</section>
		);
	}

	private onPermissionFormSave = (permissions: ICustomPermission[]) => {

		const targetUserPermissions = cloneDeep(this.state.targetUserPermissions);

		permissions.forEach((permission) => {

			const existingPermissionIndex = targetUserPermissions.findIndex((existingPermission) => existingPermission.permissionId.equals(permission.permissionId));

			if (existingPermissionIndex > -1) {

				targetUserPermissions.splice(existingPermissionIndex, 1, permission);
			}
			else {

				targetUserPermissions.push(permission);
			}

		});

		this.setState({targetUserPermissions, isPermissionFormOpen: false});

		if (this.permissionFormReference) {

			this.permissionFormReference.resetForm();
		}

		this.props.onChange(targetUserPermissions.map((customPermission) => customPermission.permission));
	}

	private onPermissionFormSaveClick = () => {

		if (this.permissionFormReference) {

			this.permissionFormReference.saveForm();
		}
	}

	private onPermissionFormCancel = () => {

		if (this.permissionFormReference) {

			this.permissionFormReference.resetForm();
		}

		this.setState({
			managingPermission: null,
			isPermissionFormOpen: false
		});
	}

	private onPermissionNewClick = () => {

		this.setState({
			managingPermission: null,
			isPermissionFormOpen: true
		});
	}

	private onPermissionEditClick = (permission: IPermissionRow) => {

		this.setState({
			managingPermission: permission,
			isPermissionFormOpen: true
		});
	}

	private onPermissionDeleteClick = (_event: React.MouseEvent<HTMLElement, MouseEvent>, permission: IPermissionRow) => {

		const targetUserPermissions = cloneDeep(this.state.targetUserPermissions);
		const targePermissionIndex = targetUserPermissions.findIndex((targetPermission) => permission.permissionId.equals(targetPermission.permissionId));

		if (targePermissionIndex > -1) {

			targetUserPermissions.splice(targePermissionIndex, 1);
		}

		this.setState({
			targetUserPermissions
		}, () => {
			this.props.onChange(this.state.targetUserPermissions.map((updatedPermission) => updatedPermission.permission));
		});
	}

	private getRightColumnValue = (right: Right): string => {

		switch (right) {
			case Right.Create:
				return "Create";
			case Right.Read:
				return "Read";
			case Right.Update:
				return "Update";
			case Right.Delete:
				return "Delete";
			case Right.AssignOwn:
				return "Assign Own";
			case Right.AssignAny:
				return "Assign Any";
		}

		return "";
	}

	private getTypeColumnValue = (recordType?: string): string => {

		switch (recordType) {
			case User.$type:
				return "User";
			case ComponentTemplate.$type:
				return "Template";
			case AdminPagePermission.$type:
				return "Page";
		}

		return "ALL";
	}

	private getRecordsColumnValue = (permission: ICustomPermission): React.ReactNode => {

		const recordId = (permission.permission as SingleRecordPermission).recordId;
		const recordIds = (permission.permission as MultipleRecordPermission).recordIds;
		const event = (permission.permission as FuturePermission).event;

		if (event !== undefined) {

			return `All future records when using right: ${this.getRightColumnValue(event)}`;
		}
		else if (recordId !== undefined) {

			if (permission.section !== undefined) {

				if (permission.section.published !== undefined) {

					return (
						<div>
							<span>1 Record - </span>
							<Link to={`/section/${permission.section.id}`}>
								{permission.section.published.name}
							</Link>
						</div>
					);
				}
				else if (permission.section.draft !== undefined) {

					return (
						<div>
							<span>1 Record - </span>
							<Link to={`/section/${permission.section.id}`}>
								{permission.section.draft.name}
							</Link>
						</div>
					);
				}
			}
			return "1 Record";
		}
		else if (recordIds !== undefined) {

			return `${recordIds.length} Record${recordIds.length === 1 ? "" : "s"}`;
		}
		else if (permission.permission.recordType === AdminPagePermission.$type) {

			return "Admin";
		}

		return "All Records";
	}

	private formatPermissionData = (permissions: ICustomPermission[]): IPermissionRow[] => {

		const permissionRows: IPermissionRow[] = [];

		permissions.forEach((permission) => {

			permissionRows.push(
				Object.assign(
					cloneDeep(permission),
					{
						type: this.getTypeColumnValue(permission.permission.recordType),
						right: this.getRightColumnValue(permission.permission.right),
						records: this.getRecordsColumnValue(permission)
					} as IPermissionRow
				)
			);
		});

		return permissionRows;
	}
}
